import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import LoadingComponent from '../../components/common/LoadingComponent';
import NotFoundPage from '../../components/home/NotFoundPage';
import { redeemUpsellToken } from '../../actions';
import LabSelector from './diagnostics/LabSelector';
import DoctorListContainer from './teleconsult/DoctorListContainer';
import { tabFormatter } from '../../utils';

/**
 * Landing route for upsell deeplinks: /upsell?t=<OTT>
 * Redeems the one-time token, logs the user in, then replaces the URL with
 * the screen mapped from the response's `flow` key so the OTT doesn't stay
 * in history. Any other query
 * params (e.g. `cartId`, `verticalId`) are forwarded to the target screen.
 */
const UpsellEntry = ({ history, location, dispatch }) => {
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ott = params.get('t');

    if (!ott) {
      setError('Missing access token. Please open the link again.');
      return;
    }

    params.delete('f');
    params.delete('t');

    dispatch(
      redeemUpsellToken(
        ott,
        (authToken, flow, data) => {
          // Target screen comes from the redeem response's `flow` key.
          const screen = tabFormatter(flow);
          // doctorRecommendation: the teleconsult page reads `verticalId` from
          // the query and opens that vertical's doctor listing.
          if (data?.verticalId) {
            params.set('verticalId', data.verticalId);
          }
          // labsCart: the lab selector fetches partners for this cart.
          if (data?.labsCartId) {
            params.set('cartId', data.labsCartId);
          }
          const cartId = params.get('cartId');
          const forwardedQuery = params.toString();
          if (!screen) {
            setError('This link is invalid. Please open the link again.');
            return;
          }
          history.replace(
            `${screen}${forwardedQuery ? `?${forwardedQuery}` : ''}`,
            cartId ? { cartId } : undefined
          );
        },
        () => setError('Could not authenticate. Please try again.')
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className="flex flex-col flex-1 justify-center items-center text-center p-24px">
        <p className="text-16 font-semibold">{error}</p>
      </div>
    );
  }

  return <LoadingComponent />;
};

const ConnectedUpsellEntry = connect()(UpsellEntry);

const UpsellNavigator = ({ loginState, authToken, history, location }) => {
  const isLanding = location.pathname.replace(/\/$/, '') === '/upsell';

  if (!isLanding && (loginState !== 'LOGGED_IN' || !authToken)) {
    history.push('/');
    return null;
  }

  return (
    <Switch>
      <Route path="/upsell" exact component={ConnectedUpsellEntry} />
      <Route path="/upsell/diagnostics" exact component={LabSelector} />
      <Route path="/upsell/teleconsult" component={DoctorListContainer} />
      <Route path="*" component={NotFoundPage} />
    </Switch>
  );
};

const mapStateToProps = (state) => ({
  authToken: state.user.authToken,
  loginState: state.app.loginState,
});

export default connect(mapStateToProps)(UpsellNavigator);
